import React, { forwardRef } from "react";
import { Form } from "react-bootstrap";

export interface RadioOption {
  /** Value of the radio button */
  value: string | number;
  /** Label shown next to the button */
  label: string;
  /** Disable a specific radio option */
  isDisabled?: boolean;
}

export interface RadioButtonGroupProps
  extends Omit<
    React.ComponentProps<typeof Form.Check>,
    "type" | "label" | "ref"
  > {
  /** Main label for the group */
  label?: string;
  /** Show red asterisk if required */
  isRequired?: boolean;
  /** Options to render */
  options: RadioOption[];
  /** Bootstrap group props */
  groupProps?: React.ComponentProps<typeof Form.Group>;
  /** Props for the main label */
  labelProps?: React.ComponentProps<typeof Form.Label>;
  /** Validation error */
  error?: string;
  name: string;
  value: string;
}

const RadioButtonGroup = forwardRef<HTMLInputElement, RadioButtonGroupProps>(
  (
    {
      label,
      isRequired = false,
      options,
      groupProps = {},
      labelProps = {},
      error,
      name,
      value,
      ...checkProps
    },
    ref
  ) => {
    const { controlId, ...otherGroupProps } = groupProps;
    return (
      <>
        <Form.Group controlId={controlId} {...otherGroupProps}>
          {label && (
            <Form.Label {...labelProps}>
              {label} {isRequired && <span style={{ color: "red" }}>*</span>}
            </Form.Label>
          )}
          <div>
            {options.map((opt) => (
              <Form.Check
                key={opt.value}
                type="radio"
                name={name}
                label={opt.label}
                value={opt.value}
                checked={value === opt.value}
                disabled={opt.isDisabled}
                ref={ref}
                {...checkProps}
              />
            ))}
          </div>
        </Form.Group>
        {error && (
          <span
            style={{ height: "20px", fontSize: "13px" }}
            className="text-danger mt-1"
          >
            {error}
          </span>
        )}
      </>
    );
  }
);

RadioButtonGroup.displayName = "RadioButtonGroup";
export default RadioButtonGroup;
